import { useEffect, useState, type CSSProperties } from 'react';

import { Button } from '../components/ui/button';

const STORAGE_KEY = 'minirag-tour-seen';
const REPLAY_EVENT = 'minirag:replay-tour';

function hasSeenTour(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    // Storage blocked (private browsing, locked-down profile) — fail closed
    // rather than nag on every load.
    return true;
  }
}

function markTourSeen() {
  try {
    localStorage.setItem(STORAGE_KEY, '1');
  } catch {
    // Nothing to do — the tour just runs again next visit, which is fine.
  }
}

/** Re-opens the tour from anywhere (wired to a command-palette action), the
 * same way `openCommandPalette` lets a distant button open that component
 * without lifting its state. */
export function replayTour() {
  window.dispatchEvent(new Event(REPLAY_EVENT));
}

interface Step {
  /** Must match a `data-tour="..."` attribute already in AppShell. */
  selector: string;
  title: string;
  description: string;
  /** Which side of the highlighted element the callout opens toward. */
  placement: 'right' | 'bottom';
}

const STEPS: Step[] = [
  {
    selector: '[data-tour="sidebar-nav"]',
    title: 'Find your way around',
    description:
      'Assistants, Knowledge, Conversations and everything else for this workspace lives here.',
    placement: 'right',
  },
  {
    selector: '[data-tour="search"]',
    title: 'Jump anywhere, fast',
    description:
      'Press ⌘K (Ctrl+K on Windows) any time to search pages or run a quick action, like switching themes.',
    placement: 'bottom',
  },
];

const CALLOUT_WIDTH = 288; // w-72
const VIEWPORT_MARGIN = 16;
const GAP = 12;

/**
 * A lightweight, first-run-only orientation — two steps, not a full
 * onboarding wizard. Only runs at `lg` widths and up, since both targets
 * (the desktop sidebar, the desktop search button) are hidden below that
 * breakpoint and have nothing meaningful to highlight on a phone.
 *
 * The dimmed backdrop is one `box-shadow: 0 0 0 9999px` on a small
 * absolutely-positioned box sized to the target's own bounding rect — an
 * "inverted spotlight" that needs no clip-path/SVG mask, and stays correct
 * across themes since it doesn't touch the target element itself.
 */
export function GuidedTour() {
  const [stepIndex, setStepIndex] = useState<number | null>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (hasSeenTour()) return;
    // Let the shell finish its first paint (and any layout-affecting fonts/
    // icons load) before measuring anything.
    const id = window.setTimeout(() => {
      if (window.matchMedia('(min-width: 1024px)').matches) setStepIndex(0);
    }, 600);
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => {
    function onReplay() {
      setStepIndex(0);
    }
    window.addEventListener(REPLAY_EVENT, onReplay);
    return () => window.removeEventListener(REPLAY_EVENT, onReplay);
  }, []);

  useEffect(() => {
    if (stepIndex === null) {
      setRect(null);
      return;
    }
    const step = STEPS[stepIndex];
    if (!step) return;

    function measure() {
      const el = document.querySelector(step!.selector);
      setRect(el ? el.getBoundingClientRect() : null);
    }
    measure();
    window.addEventListener('resize', measure);
    // `true` — the sidebar/main content have their own internal scroll
    // regions, which don't bubble a plain (non-capturing) scroll listener.
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [stepIndex]);

  function finish() {
    markTourSeen();
    setStepIndex(null);
  }

  function next() {
    if (stepIndex === null) return;
    if (stepIndex + 1 >= STEPS.length) finish();
    else setStepIndex(stepIndex + 1);
  }

  if (stepIndex === null) return null;
  const step = STEPS[stepIndex];
  // The target genuinely isn't on screen (window shrank below `lg` mid-tour,
  // or the selector is stale) — end rather than spotlight nothing.
  if (!step || !rect) return null;

  const pad = 8;
  const highlightStyle: CSSProperties = {
    position: 'fixed',
    top: rect.top - pad,
    left: rect.left - pad,
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
    borderRadius: 12,
    boxShadow: '0 0 0 9999px rgba(4, 8, 20, 0.65)',
    pointerEvents: 'none',
    zIndex: 60,
    transition: 'top 200ms ease, left 200ms ease, width 200ms ease, height 200ms ease',
  };

  const rawTop = step.placement === 'bottom' ? rect.bottom + pad + GAP : rect.top;
  const rawLeft = step.placement === 'right' ? rect.right + pad + GAP : rect.left;
  const calloutTop = Math.max(VIEWPORT_MARGIN, rawTop);
  const calloutLeft = Math.min(
    Math.max(VIEWPORT_MARGIN, rawLeft),
    window.innerWidth - CALLOUT_WIDTH - VIEWPORT_MARGIN,
  );

  return (
    <>
      <div style={highlightStyle} aria-hidden />
      <div
        role="dialog"
        aria-label={step.title}
        className="fixed z-[61] rounded-[var(--radius-card)] border border-border bg-surface p-4 shadow-2xl shadow-black/40"
        style={{ top: calloutTop, left: calloutLeft, width: CALLOUT_WIDTH }}
      >
        <p className="font-medium">{step.title}</p>
        <p className="mt-1 text-sm text-muted-foreground">{step.description}</p>
        <div className="mt-4 flex items-center justify-between">
          <button
            type="button"
            onClick={finish}
            className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          >
            Skip
          </button>
          <div className="flex items-center gap-3">
            <span className="label-micro">
              {stepIndex + 1}/{STEPS.length}
            </span>
            <Button size="sm" onClick={next}>
              {stepIndex + 1 >= STEPS.length ? 'Done' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
